import React, { useState } from 'react';
import { X, Calendar } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';

interface CareerGoalFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

const GOAL_TYPES = [
  { value: 'promotion', label: 'Get promoted to a specific role' },
  { value: 'transition', label: 'Transition into a new industry' },
  { value: 'remote', label: 'Land a remote job' },
  { value: 'consulting', label: 'Start a consulting business' },
  { value: 'leadership', label: 'Improve leadership skills' }
];

export function CareerGoalForm({ onClose, onSuccess }: CareerGoalFormProps) {
  const { user } = useAuth(); // Get the current authenticated user
  const [formData, setFormData] = useState({
    title: '',
    type: '',
    targetDate: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.type) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!user) {
      toast.error('You must be logged in to create a goal');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('career_goals')
        .insert({
          title: formData.title.trim(),
          type: formData.type,
          target_date: formData.targetDate || null,
          status: 'active',
          progress: 0,
          user_id: user.id // Add the user ID to associate the goal with the user
        });

      if (error) throw error;
      
      toast.success('Career goal added successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating goal:', error);
      toast.error('Failed to create goal');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-black">Set Career Goal</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Goal Type*
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
              required
            >
              <option value="">Select goal type</option>
              {GOAL_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Specific Goal*
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
              placeholder="e.g., Become a Senior Software Engineer at a tech company"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Target Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={formData.targetDate}
                onChange={(e) => setFormData(prev => ({ ...prev, targetDate: e.target.value }))}
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-black focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#800020] text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Setting Goal...' : 'Set Goal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}